// React Query hooks for the skip-inventory surface.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { queryKeys } from '../../../api/queryKeys';
import type { SkipInventoryItem, SkipInventoryListResponse } from '../../../types/api';

interface SkipInventoryFilters {
  page?: number;
  limit?: number;
  status?: string;
  skip_type?: string;
  /** Bug 4: filter inventory to units bound to a particular stock-size
   *  row (skip_stock_sizes.id integer). Drives the inventory dropdown
   *  on Job Create / Process Enquiry / Process Exchange flows so the
   *  list shows only units that match the chosen size. */
  skip_size_id?: number | null;
  /** enc_id of the bound skip_stock_sizes row. Same filter as
   *  skip_size_id but uses the encrypted form held by the form pages
   *  (the /reference/skip-sizes catalogue returns enc_ids). */
  skip_size_enc_id?: string | null;
  q?: string;
}

export function useSkipInventory(filters: SkipInventoryFilters = {}) {
  return useQuery<SkipInventoryListResponse>({
    queryKey: queryKeys.skipInventory.list(filters as Record<string, unknown>),
    queryFn: async () => {
      const res = await apiClient.get<SkipInventoryListResponse>(endpoints.skipInventory.list, {
        params: filters,
      });
      return res.data;
    },
    staleTime: 30_000,
  });
}

export function useSkipInventoryItem(encId: string | null | undefined) {
  return useQuery<{ item: SkipInventoryItem }>({
    queryKey: queryKeys.skipInventory.detail(encId ?? ''),
    queryFn: async () => {
      if (!encId) throw new Error('useSkipInventoryItem called without enc_id');
      const res = await apiClient.get<{ item: SkipInventoryItem }>(
        endpoints.skipInventory.detail(encId),
      );
      return res.data;
    },
    enabled: !!encId,
    staleTime: 30_000,
  });
}

export function useCreateSkipInventoryItem() {
  const qc = useQueryClient();
  return useMutation<{ item: SkipInventoryItem }, Error, Record<string, unknown>>({
    mutationFn: async (body) => {
      const res = await apiClient.post<{ item: SkipInventoryItem }, Record<string, unknown>>(
        endpoints.skipInventory.create,
        body,
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.skipInventory.all });
    },
  });
}

export function useUpdateSkipInventoryItem(encId: string) {
  const qc = useQueryClient();
  return useMutation<{ item: SkipInventoryItem }, Error, Record<string, unknown>>({
    mutationFn: async (body) => {
      const res = await apiClient.put<{ item: SkipInventoryItem }, Record<string, unknown>>(
        endpoints.skipInventory.update(encId),
        body,
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.skipInventory.all });
      void qc.invalidateQueries({ queryKey: queryKeys.skipInventory.detail(encId) });
    },
  });
}

export function useDeleteSkipInventoryItem(encId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, void>({
    mutationFn: async () => {
      await apiClient.delete(endpoints.skipInventory.delete(encId));
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.skipInventory.all });
    },
  });
}

export interface InventoryHistoryEntry {
  event: string;
  job_enc_id: string;
  job_uuid: string;
  job_number: string;
  actor_name: string | null;
  notes: string | null;
  created_at: string;
}

/**
 * GET /api/v1/skip/inventory/:enc_id/history — Phase 6H. Returns the
 * full audit trail of every state change on this unit (assigned /
 * marked_out / marked_available / freed / child_assigned), newest
 * first, with the originating job number + actor name pre-joined.
 */
export function useInventoryHistory(encId: string | null | undefined) {
  return useQuery<{ history: InventoryHistoryEntry[] }>({
    queryKey: queryKeys.skipInventory.history(encId ?? ''),
    queryFn: async () => {
      if (!encId) throw new Error('useInventoryHistory called without enc_id');
      const res = await apiClient.get<{ history: InventoryHistoryEntry[] }>(
        endpoints.skipInventory.history(encId),
      );
      return res.data;
    },
    enabled: !!encId,
    staleTime: 30_000,
  });
}
